import React from "react";
import {Trans, useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {Link, Stack, Typography} from "@mui/material";
import {KeyValuePair} from "../base/KeyValuePair";
import {NotificationCard} from "./NotificationCard";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {getResourceErrorMessage, isResourceLoading} from "../../utils/resourceUtils";
import {Order} from "../../types/domain/order";
import {changeStatus} from "../../services/orderService";
import {OrderStatus} from "../../types/domain/orderStatus";
import {getOrderConfirmPageUrl, getOrderPeriod} from "../../utils/orderUtils";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {getUserLimitedInfoById} from "../../services/userService";
import {useQuery} from "@tanstack/react-query";
import {UserLimitedInfo} from "../../types/domain/user";

export interface Props {
    order: Order;
}

export const OrderNotificationCard: React.FC<Props> = ({order}) => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const msUtils = useMeasureSystemUtils();
    const orderManagement = useResourceManagement({changeStatus});

    const {data: submittedByUser} = useQuery<UserLimitedInfo>({
        queryKey: ["user-limited-info", order.userId],
        queryFn: async () => await getUserLimitedInfoById(order.userId),
        staleTime: 1000 * 60 * 5,
    });

    return (
        <NotificationCard
            header={t("notifications.order_notification.title", {order: order.id})}
            date={getOrderPeriod(order, dateFormat)}
            isPending={order.state === OrderStatus.AWAITING_CONFIRMATION}
            isLoading={isResourceLoading(orderManagement.resource)}
            error={getResourceErrorMessage(orderManagement.resource)}
        >
            <Stack direction="column" gap="0.5rem">
                <KeyValuePair header={t("notifications.order_notification.order")}>
                    <Typography>{order.id}</Typography>
                </KeyValuePair>
                <KeyValuePair header={t("notifications.order_notification.nb_fields")}>
                    <Typography>{order.fields.length}</Typography>
                </KeyValuePair>
                <KeyValuePair header={t("notifications.order_notification.area")}>
                    <Typography>
                        {order.areaInHa ? msUtils.getArea(order.areaInHa).toFixed(2) : 0} {msUtils.getAreaUnits()}
                    </Typography>
                </KeyValuePair>
                {order.discount > 0 && (
                    <>
                        <KeyValuePair header={t("notifications.order_notification.price")}>
                            <Typography>{order.price}</Typography>
                        </KeyValuePair>
                        <KeyValuePair header={t("notifications.order_notification.discount")}>
                            <Typography>{order.discount} %</Typography>
                        </KeyValuePair>
                    </>
                )}
                <KeyValuePair header={t("notifications.order_notification.final_price")}>
                    <Typography>{order.finalPrice}</Typography>
                </KeyValuePair>
                <KeyValuePair header={t("notifications.order_notification.submitted_by")}>
                    <Typography>{`${submittedByUser?.firstName ?? ""} ${submittedByUser?.lastName ?? ""}`}</Typography>
                </KeyValuePair>
                <Typography style={{marginTop: "1.5rem"}}>
                    <Trans
                        i18nKey="notifications.order_notification.click_here_to_confirm"
                        components={{
                            here: (
                                <Link
                                    component={RouterLink}
                                    to={getOrderConfirmPageUrl(order.companyId, order.id)}
                                    style={{color: "blue", textDecoration: "none"}}
                                />
                            ),
                        }}
                    />
                </Typography>
            </Stack>
        </NotificationCard>
    );
};
